import type { SDKStorage } from '../storage/types.js';

/**
 * Durable mirror for the client caches (sdk ADR-92811): the storage seam
 * under the dedicated `la:cache:` namespace, never the credentials namespace.
 * Each cache owns one sub-namespace (`la:cache:<owner>`), and every entry in
 * it lives under the acting identity (`la:cache:<owner>@<identity>/<key>`,
 * sdk ADR-a9d91): hydration reads only the bound identity's entries, so a
 * page written under one account is never restored for another.
 *
 * Synchronous (the seam is sync by contract): write-through on mutation,
 * hydrate on bind. A blob written by a different schema version, unparseable,
 * or stored outside an identity namespace is REMOVED and treated as absent —
 * a cache snapshot is a paint skeleton, never trusted state (no-fallbacks.md:
 * the degradation is the designed cache-miss refetch, nothing is silently
 * served).
 */
export const CACHE_NAMESPACE = 'la:cache:';
export const CACHE_SNAPSHOT_VERSION = 2;

export interface DurableCacheEntry {
  version: number;
  value: unknown;
}

/** The storage key of one cache entry: `la:cache:<owner>@<identity>/<key>`. */
export function durableCacheKey(owner: string, identity: string, key: string): string {
  return `${CACHE_NAMESPACE}${owner}@${identity}/${key}`;
}

export class DurableCache<K> {
  private identity: string | null = null;
  private readonly ownerPrefix: string;

  constructor(
    private readonly storage: SDKStorage | null,
    /** The owner's sub-namespace, e.g. `history:`. */
    private readonly owner: string,
    /** Re-insert a hydrated entry into the in-memory store, by its cache key. */
    private readonly restore: (key: string, value: K) => void,
  ) {
    this.ownerPrefix = `${CACHE_NAMESPACE}${owner}`;
  }

  /**
   * Bind the acting identity and load its stored snapshots into memory.
   * Entries outside any identity namespace (version 1) are deleted here.
   */
  bind(identity: string): void {
    if (identity === '' || identity.includes('/')) {
      throw new Error(`DurableCache(${this.owner}): invalid identity ${JSON.stringify(identity)}`);
    }
    this.identity = identity;
    if (!this.storage) return;
    const identityPrefix = durableCacheKey(this.owner, identity, '');
    for (const fullKey of this.storage.keys()) {
      if (!fullKey.startsWith(this.ownerPrefix)) continue;
      if (!fullKey.startsWith(`${this.ownerPrefix}@`)) {
        this.storage.removeItem(fullKey);
        continue;
      }
      if (!fullKey.startsWith(identityPrefix)) continue;
      const raw = this.storage.getItem(fullKey);
      if (raw === null) continue;
      try {
        const parsed = JSON.parse(raw) as DurableCacheEntry;
        if (parsed.version !== CACHE_SNAPSHOT_VERSION) {
          this.storage.removeItem(fullKey);
          continue;
        }
        this.restore(fullKey.slice(identityPrefix.length), parsed.value as K);
      } catch {
        this.storage.removeItem(fullKey);
      }
    }
  }

  writeThrough(key: string, value: K): void {
    if (!this.storage) return;
    const storageKey = this.boundKey(key);
    try {
      this.storage.setItem(
        storageKey,
        JSON.stringify({ version: CACHE_SNAPSHOT_VERSION, value } satisfies DurableCacheEntry),
      );
    } catch {
      // no-fallbacks.md exception: quota/private-mode failures degrade the
      // SNAPSHOT only — the in-memory cache is untouched and remains correct.
    }
  }

  remove(key: string): void {
    if (!this.storage) return;
    this.storage.removeItem(this.boundKey(key));
  }

  /** Drop this owner's durable entries, under every identity, whose cache key starts with `keyPrefix`. */
  evictDurablePrefix(keyPrefix: string): void {
    if (!this.storage) return;
    for (const fullKey of this.storage.keys()) {
      if (!fullKey.startsWith(`${this.ownerPrefix}@`)) continue;
      const slash = fullKey.indexOf('/', this.ownerPrefix.length);
      if (slash === -1) continue;
      if (fullKey.slice(slash + 1).startsWith(keyPrefix)) this.storage.removeItem(fullKey);
    }
  }

  /** Drop every durable entry this owner wrote under `identity`. */
  evictIdentityDurable(identity: string): void {
    if (!this.storage) return;
    const identityPrefix = durableCacheKey(this.owner, identity, '');
    for (const fullKey of this.storage.keys()) {
      if (fullKey.startsWith(identityPrefix)) this.storage.removeItem(fullKey);
    }
  }

  /** Drop every durable entry this owner wrote, under every identity, and no other owner's. */
  evictAllDurable(): void {
    if (!this.storage) return;
    for (const fullKey of this.storage.keys()) {
      if (fullKey.startsWith(this.ownerPrefix)) this.storage.removeItem(fullKey);
    }
  }

  private boundKey(key: string): string {
    if (this.identity === null) {
      throw new Error(`DurableCache(${this.owner}): no identity bound for key ${key}`);
    }
    return durableCacheKey(this.owner, this.identity, key);
  }
}
